import { Check } from 'lucide-react'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import MiniCalendar from './MiniCalendar'
import { DEFAULT_FILTERS, TYPE_OPTIONS } from '../../../utils/eventFilters'

const DATE_SHORTCUTS = [
  { kind: 'today', label: 'Today' },
  { kind: 'tomorrow', label: 'Tomorrow' },
  { kind: 'week', label: 'This week' },
  { kind: 'next-week', label: 'Next week' },
  { kind: 'two-weeks', label: 'Next 2 weeks' },
]

const FacetHeading = ({ children }) => (
  <p className="text-xs font-semibold uppercase tracking-[0.08em] text-gray-400">{children}</p>
)

const RadioRow = ({ value, checked, label, count }) => (
  <label className="flex cursor-pointer items-center gap-2.5 text-sm text-gray-700">
    <RadioGroupItem
      value={value}
      className="h-[18px] w-[18px] shrink-0 border-2 border-gray-400 bg-white shadow-none transition data-[state=checked]:border-primary-600"
    />
    <span className={`flex-1 ${checked ? 'font-semibold text-primary-800' : ''}`}>{label}</span>
    {count != null && <span className="text-xs font-medium text-gray-400">{count}</span>}
  </label>
)

const CheckRow = ({ checked, label, count, onChange }) => (
  <label className="flex cursor-pointer items-center gap-2.5 text-sm text-gray-700">
    <input type="checkbox" checked={checked} onChange={onChange} className="peer sr-only" />
    <span
      className={`flex h-[17px] w-[17px] shrink-0 items-center justify-center rounded border-[1.5px] transition peer-focus-visible:ring-2 peer-focus-visible:ring-primary-500 peer-focus-visible:ring-offset-1 ${
        checked ? 'border-primary-600 bg-primary-600' : 'border-gray-400 bg-white'
      }`}
    >
      <Check size={11} strokeWidth={3} className={`text-cream-50 transition-opacity ${checked ? 'opacity-100' : 'opacity-0'}`} />
    </span>
    <span className={`flex-1 ${checked ? 'font-semibold text-primary-800' : ''}`}>{label}</span>
    {count != null && <span className="text-xs font-medium text-gray-400">{count}</span>}
  </label>
)

// The four facet sections, shared between the desktop sidebar and the
// mobile EventFilterSheet. The first section carries no top border so the
// wrapper decides how the stack starts.
export const EventFilterFacets = ({ filters, onChange, locationCounts, typeCounts }) => {
  const date = filters.date || DEFAULT_FILTERS.date

  const setLocation = (location) => onChange({ ...filters, location })
  const setDateKind = (kind) =>
    onChange({ ...filters, date: date.kind === kind ? { kind: 'all' } : { kind } })
  const toggleType = (value) =>
    onChange({
      ...filters,
      types: filters.types.includes(value)
        ? filters.types.filter((t) => t !== value)
        : [...filters.types, value],
    })
  const setCost = (cost) => onChange({ ...filters, cost })

  return (
    <>
      <section className="flex flex-col gap-3">
        <FacetHeading>Location</FacetHeading>
        <RadioGroup
          value={filters.location}
          onValueChange={setLocation}
          aria-label="Location"
          className="flex flex-col gap-2"
        >
          <RadioRow value="all" checked={filters.location === 'all'} label="All locations" />
          <RadioRow
            value="online"
            checked={filters.location === 'online'}
            label="Online"
            count={locationCounts.online}
          />
          {locationCounts.cities.map(({ city, count }) => (
            <RadioRow
              key={city}
              value={city}
              checked={filters.location === city}
              label={city}
              count={count}
            />
          ))}
        </RadioGroup>
      </section>

      <section className="flex flex-col gap-3 border-t border-gray-100 pt-5">
        <FacetHeading>Date</FacetHeading>
        <div className="flex flex-wrap gap-2">
          {DATE_SHORTCUTS.map(({ kind, label }) => (
            <button
              key={kind}
              type="button"
              aria-pressed={date.kind === kind}
              onClick={() => setDateKind(kind)}
              className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-xs transition ${
                date.kind === kind
                  ? 'border-primary-600 bg-primary-600 font-semibold text-cream-50'
                  : 'border-gray-200 bg-primary-50 font-medium text-gray-700 hover:bg-primary-100'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <MiniCalendar
          rangeStart={date.kind === 'custom' ? date.start : null}
          rangeEnd={date.kind === 'custom' ? date.end : null}
          onSelectRange={(start, end) =>
            onChange({ ...filters, date: { kind: 'custom', start, end } })
          }
        />
      </section>

      <section className="flex flex-col gap-3 border-t border-gray-100 pt-5">
        <FacetHeading>Type</FacetHeading>
        <div className="flex flex-col gap-2">
          {TYPE_OPTIONS.map(({ value, label }) => (
            <CheckRow
              key={value}
              checked={filters.types.includes(value)}
              label={label}
              count={typeCounts[value] || 0}
              onChange={() => toggleType(value)}
            />
          ))}
        </div>
      </section>

      <section className="flex flex-col gap-3 border-t border-gray-100 pt-5">
        <FacetHeading>Cost</FacetHeading>
        <div role="group" aria-label="Cost" className="grid grid-cols-3 gap-0.5 rounded-lg border border-gray-200 bg-primary-50 p-[3px]">
          {['all', 'free', 'paid'].map((cost) => (
            <button
              key={cost}
              type="button"
              aria-pressed={filters.cost === cost}
              onClick={() => setCost(cost)}
              className={`rounded-md py-1.5 text-xs capitalize transition ${
                filters.cost === cost
                  ? 'bg-white font-semibold text-primary-800 shadow-sm'
                  : 'font-medium text-gray-600 hover:text-primary-700'
              }`}
            >
              {cost}
            </button>
          ))}
        </div>
      </section>
    </>
  )
}

const EventFilterSidebar = ({ filters, onChange, locationCounts, typeCounts }) => (
  <aside
    aria-label="Filter events"
    className="hidden w-full shrink-0 flex-col gap-5 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm lg:sticky lg:top-24 lg:flex lg:w-[280px]"
  >
    <EventFilterFacets
      filters={filters}
      onChange={onChange}
      locationCounts={locationCounts}
      typeCounts={typeCounts}
    />

    <section className="border-t border-gray-100 pt-5">
      <button
        type="button"
        className="text-sm font-semibold text-primary-600 hover:text-primary-700 hover:underline"
        onClick={() => onChange({ ...DEFAULT_FILTERS, date: { kind: 'all' }, types: [] })}
      >
        Clear all filters
      </button>
    </section>
  </aside>
)

export default EventFilterSidebar
